import { useCallback, useMemo } from 'react';
import { ResearchState } from '@/contexts/research/ResearchContext';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import { UseMutationResult } from '@tanstack/react-query';

interface UseResearchSubmissionProps {
  state: ResearchState;
  onGenerateResearch: () => void;
  onResearchGenerated?: (research: any) => void;
  resetForm: () => void;
  generateResearchMutation: UseMutationResult<any, Error, any, unknown>;
  selectedProjectId?: string | null;
}

export const useResearchSubmission = ({
  state,
  onGenerateResearch,
  onResearchGenerated,
  resetForm,
  generateResearchMutation,
  selectedProjectId,
}: UseResearchSubmissionProps) => {
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();

  const handleGenerateResearch = useCallback(async () => {
    // Only require the prompt to have minimum 1 word - no research type required
    if (!state.researchPrompt.trim()) {
      return;
    }

    // ✅ UPFRONT CREDIT CHECK - Prevent operation if no credits available
    if (usageData.remainingBalance === 0) {
      console.log('[ResearchSubmission] Insufficient credits - blocking research generation and showing modal');
      openOutOfCreditsModal();
      return; // Clean early exit - no cascading errors
    }

    console.log(`[ResearchSubmission] Credits available (${usageData.remainingBalance}) - proceeding with research generation`);

    // Instead of directly executing the mutation here, call the parent's handler
    // which will show the modal and handle the generation properly
    onGenerateResearch();
    
    // The actual mutation will be handled by the modal/parent component
  }, [state.researchPrompt, onGenerateResearch, usageData.remainingBalance, openOutOfCreditsModal]);

  // Only require minimum 1 word - no research type requirement
  const isReadyToGenerate = useMemo(() => {
    const wordCount = state.researchPrompt.trim().split(/\s+/).filter(word => word.length > 0).length;
    return wordCount >= 1; // Simple 1-word minimum requirement
  }, [state.researchPrompt]);

  return {
    handleGenerateResearch,
    isReadyToGenerate,
    isLoading: false, // Loading state will be handled by the modal
  };
};
